import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Text, TextType } from 'ui-components/components/text';

import { ProductGrid } from './product-search/product-grid';
import { purchaseOrderProperties } from '../../data/purchase-order-properties';
import { purchaseOrderStatuses } from '../../data/purchase-order-statuses';
import { usePurchaseOrderContext } from '../../purchase-order-context';
import { ProductSearchInput } from './product-search/product-search/product-search-input';
import { messages } from './purchase-order-details-messages';
import styles from './purchase-order-details.styles.module.less';
import { PurchaseOrderCostSummary } from './purchase-order-cost-summary';
import { PurchaseOrderPaymentDetails } from './purchase-order-payment-details';
import { PurchaseOrderShippingDetails } from './purchase-order-shipping-details';
import { getPurchaseOrderSchema } from '../../form-schema';
import { SupplierDetails } from '../preview/supplier-details';
import { PurchaseOrderExpandedSectionProvider } from '../../purchase-order-expandable-section-provider';
import { EditableSupplierSection } from './components/editable-supplier-section';

/**
 * @module PurchaseOrderDetails
 * @description A component that renders the main form for creating or editing a purchase order.
 * This component uses the PurchaseOrderContext to access and update the purchase order state
 * and retrieve dropdown options like suppliers, warehouses, payment terms, and currencies.
 *
 * The form is divided into three main sections:
 * 1. Supplier Information - Select supplier and warehouse
 * 2. Shipping Details - Enter shipping carrier, tracking number, payment terms, currency,
 *    order ID, order date, reference number, and notes to supplier
 * 3. Products - Search for and add products to the purchase order
 *
 * All form fields are connected to the purchase order state through the context.
 * @param {object} props - Component props
 * @param {object} props.currentSupplier - The current supplier data object
 * @param {boolean} props.isLoadingSupplier - Loading state for supplier data
 * @param {() => void} props.refetchSupplier - Function to refetch current supplier data
 * @returns {JSX.Element} A form with multiple sections for purchase order details
 * @example
 * <PurchaseOrderDetails currentSupplier={currentSupplier} isLoadingSupplier={isLoadingSupplier} refetchSupplier={refetchSupplier} />
 */
export const PurchaseOrderDetails = ({ currentSupplier, isLoadingSupplier, refetchSupplier }) => {
  const { purchaseOrder, setPurchaseOrder, validateForm, setFieldTouched, getFieldError, fieldsTouched } = usePurchaseOrderContext();
  const intl = useIntl();

  const productsLengthRef = useRef(purchaseOrder.products?.length || 0);

  const validateAndPreserveProductError = useCallback(
    (data, touchedField) => {
      setFieldTouched(touchedField);

      const productsFieldTouched = fieldsTouched[purchaseOrderProperties.products];
      const hasNoProducts = !data.products || data.products.length === 0;

      // If we are already showing the product validation error, keep showing it
      // by requiring products validation with the strict schema
      if (productsFieldTouched && hasNoProducts) {
        setFieldTouched(purchaseOrderProperties.products);
        validateForm(data, getPurchaseOrderSchema(true));
      } else {
        validateForm(data, getPurchaseOrderSchema(false));
      }
    },
    [fieldsTouched, setFieldTouched, validateForm],
  );

  const productsLength = purchaseOrder.products?.length || 0;

  useEffect(() => {
    const prevLength = productsLengthRef.current;
    productsLengthRef.current = productsLength;

    // Only validate when products are added or removed, not on field edits
    const hasLengthChanged = productsLength !== prevLength;
    if (!hasLengthChanged) {
      return;
    }

    if (productsLength > prevLength) {
      setFieldTouched(purchaseOrderProperties.products);

      // Mark only newly added products as touched
      for (let i = prevLength; i < productsLength; i++) {
        setFieldTouched(`products[${i}].quantity`);
        setFieldTouched(`products[${i}].cost`);
      }
    }

    validateForm(purchaseOrder, getPurchaseOrderSchema(false));
  }, [productsLength, purchaseOrder, validateForm, setFieldTouched]);

  const handleInputChange = (field, value) => {
    setPurchaseOrder((order) => {
      const updatedOrder = {
        ...order,
        [field]: value,
      };

      validateAndPreserveProductError(updatedOrder, field);
      return updatedOrder;
    });
  };

  const hasGeneralProductError = useMemo(() => {
    const productsError = getFieldError(purchaseOrderProperties.products);
    return productsError && !Array.isArray(productsError);
  }, [getFieldError]);

  const productErrors = useMemo(() => {
    const productsError = getFieldError(purchaseOrderProperties.products);
    if (!productsError || !Array.isArray(productsError)) return [];

    const errorsBySku = {};

    productsError.forEach((error, index) => {
      if (!error) return;

      const product = purchaseOrder.products[index];
      if (!product) return;

      const sku = product.sku || intl.formatMessage(messages.unknownSku);

      const quantityError = error.quantity ? intl.formatMessage(error.quantity.message) : null;
      const costError = error.cost ? intl.formatMessage(error.cost.message) : null;

      const errorMessages = [costError, quantityError].filter((item) => item);

      if (errorMessages.length > 0) {
        errorsBySku[sku] = errorsBySku[sku] ? [...errorsBySku[sku], ...errorMessages] : errorMessages;
      }
    });

    return Object.entries(errorsBySku).map(([sku, errors], index) => (
      <FormattedMessage
        key={`product-error-${sku}-${index}`}
        {...messages.productErrorsFormat}
        values={{
          sku,
          errorMessages: errors.join(', '),
        }}
      />
    ));
  }, [getFieldError, purchaseOrder.products, intl]);

  const isReadOnly = purchaseOrder.status && purchaseOrder.status !== purchaseOrderStatuses.draft;

  return (
    <PurchaseOrderExpandedSectionProvider>
      <div className={styles['edit-data-container']}>
        {isReadOnly ? (
          <SupplierDetails currentSupplier={currentSupplier} isLoadingSupplier={isLoadingSupplier} />
        ) : (
          <EditableSupplierSection currentSupplier={currentSupplier} onInputChange={handleInputChange} refetchSupplier={refetchSupplier} />
        )}
        <PurchaseOrderShippingDetails />
        <PurchaseOrderPaymentDetails />
        <PurchaseOrderCostSummary />
        <div className={styles['edit-row-container']}>
          <Text
            className={styles['edit-row-header']}
            textType={TextType.H4}
            value={isReadOnly ? messages.products : messages.addProducts}
          />
          {!isReadOnly && <ProductSearchInput />}

          {/* Handle general product error (e.g., no products added) */}
          {hasGeneralProductError ? (
            <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
              <FormattedMessage {...getFieldError(purchaseOrderProperties.products).message} />
            </InlineMessage>
          ) : null}

          {/* Handle product-specific validation errors */}
          {productErrors.map((error, index) => (
            <InlineMessage key={`inline-${index}`} type={INLINE_MESSAGE_TYPES.error}>
              {error}
            </InlineMessage>
          ))}

          <ProductGrid isReadOnly={isReadOnly} />
        </div>
      </div>
    </PurchaseOrderExpandedSectionProvider>
  );
};
